<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="68px"
    >
      <el-form-item label="名称" prop="name">
        <el-input
          v-model="queryParams.name"
          class="!w-240px"
          clearable
          placeholder="请输入设施名称"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="分类" prop="category">
        <el-input
          v-model="queryParams.category"
          class="!w-240px"
          clearable
          placeholder="请输入分类"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select
          :model-value="queryParams.status as any"
          @update:model-value="(val: any) => (queryParams.status = val)"
          class="!w-240px"
          clearable
          placeholder="请选择状态"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['hotel:facility:create']"
          plain
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column align="center" label="设施名称" prop="name" min-width="180">
        <template #default="scope">
          <el-tag type="info">{{ scope.row.name }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="设施编码" prop="code" min-width="150" />
      <el-table-column align="center" label="图标" prop="icon" width="70">
        <template #default="scope">
          <el-image
            v-if="scope.row.icon"
            :src="scope.row.icon"
            style="width: 40px; height: 40px; border-radius: 4px"
            fit="contain"
          />
          <span v-else class="text-gray-400">-</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="分类" prop="category">
        <template #default="scope">
          <el-tag :type="getCategoryTag(scope.row.category)">
            {{ scope.row.category || '未分类' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="系统内置" prop="isSystem">
        <template #default="scope">
          <el-tag v-if="scope.row.isSystem === 1" type="warning">是</el-tag>
          <el-tag v-else type="info">否</el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="排序" prop="sort" />
      <el-table-column align="center" label="状态" prop="status">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.COMMON_STATUS" :value="scope.row.status" />
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180"
      />
      <el-table-column align="center" label="操作">
        <template #default="scope">
          <el-button
            v-hasPermi="['hotel:facility:update']"
            link
            type="primary"
            @click="openForm('update', scope.row.id)"
          >
            修改
          </el-button>
          <el-button
            v-hasPermi="['hotel:facility:delete']"
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="dialogVisible" :title="dialogTitle">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="100px"
    >
      <el-form-item label="设施名称" prop="name">
        <el-input v-model="formData.name" placeholder="请输入设施名称" />
      </el-form-item>
      <el-form-item label="设施编码" prop="code">
        <el-input
          v-model="formData.code"
          :disabled="formData.isSystem === 1 && formType === 'update'"
          placeholder="如 WIFI、AIR_CONDITIONING"
          @input="(value: string) => (formData.code = value.toUpperCase())"
        />
      </el-form-item>
      <el-form-item label="分类" prop="category">
        <el-select
          :model-value="formData.category as any"
          @update:model-value="(val: any) => (formData.category = val)"
          clearable
          placeholder="请选择分类"
        >
          <el-option label="酒店设施" value="HOTEL" />
          <el-option label="客房设施" value="ROOM" />
          <el-option label="餐饮设施" value="DINING" />
          <el-option label="休闲设施" value="RECREATION" />
          <el-option label="商务设施" value="BUSINESS" />
          <el-option label="其他" value="OTHER" />
        </el-select>
      </el-form-item>
      <el-form-item label="图标" prop="icon">
        <UploadImg
          v-model="formData.icon"
          :width="'80px'"
          :height="'80px'"
          :file-size="2"
          :file-type="['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/x-icon']"
          directory="hotel/facility-icon"
        >
          <template #tip>图标建议 80×80，≤ 2MB</template>
        </UploadImg>
      </el-form-item>
      <el-form-item label="排序" prop="sort">
        <el-input-number v-model="formData.sort" :min="0" placeholder="请输入排序" />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :loading="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import * as facilityApi from '@/api/hotel/facility'
import { CommonStatusEnum } from '@/utils/constants'
import UploadImg from '@/components/UploadFile/src/UploadImg.vue'

defineOptions({ name: 'HotelFacility' })

const message = useMessage()
const { t } = useI18n()

const loading = ref(true)
const total = ref(0)
const list = ref([])

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 20,
  name: undefined,
  category: undefined,
  status: undefined
})
const queryFormRef = ref()

const getList = async () => {
  loading.value = true
  try {
    const data = await facilityApi.getFacilityPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

// 设施分类映射
const categoryTagMap: Record<string, string> = {
  HOTEL: 'primary',
  ROOM: 'success',
  DINING: 'warning',
  RECREATION: 'danger',
  BUSINESS: 'info',
  OTHER: ''
}
const getCategoryTag = (c: string) => categoryTagMap[c] || 'info'

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value.resetFields()
  handleQuery()
}

const dialogVisible = ref(false)
const dialogTitle = ref('')
const formLoading = ref(false)
const formType = ref('')
const formData = ref<facilityApi.FacilityVO>({
  id: 0,
  code: '',
  name: '',
  category: '',
  icon: undefined,
  isSystem: 0,
  sort: 0,
  status: CommonStatusEnum.ENABLE,
  createTime: undefined as any
})
const formRules = reactive({
  name: [{ required: true, message: '设施名称不能为空', trigger: 'blur' }],
  code: [
    { required: true, message: '设施编码不能为空', trigger: 'blur' },
    {
      pattern: /^[A-Z][A-Z0-9_]{1,63}$/,
      message: '请输入2～64位大写字母、数字或下划线',
      trigger: 'blur'
    }
  ],
  status: [{ required: true, message: '状态不能为空', trigger: 'change' }]
})
const formRef = ref()

const openForm = async (type: string, id?: string | number) => {
  dialogVisible.value = true
  dialogTitle.value = t('action.' + type)
  formType.value = type
  resetForm()
  if (id) {
    formLoading.value = true
    try {
      formData.value = await facilityApi.getFacility(id)
    } finally {
      formLoading.value = false
    }
  }
}

const submitForm = async () => {
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return
  formLoading.value = true
  try {
    const data = formData.value
    if (formType.value === 'create') {
      await facilityApi.createFacility(data)
      message.success(t('common.createSuccess'))
    } else {
      await facilityApi.updateFacility(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}

const resetForm = () => {
  formData.value = {
    id: 0,
    code: '',
    name: '',
    category: '',
    icon: undefined,
    isSystem: 0,
    sort: 0,
    status: CommonStatusEnum.ENABLE,
    createTime: undefined as any
  }
  formRef.value?.resetFields()
}

const handleDelete = async (id: string | number) => {
  try {
    await message.delConfirm()
    await facilityApi.deleteFacility(id)
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

onMounted(() => {
  getList()
})
</script>
